import { AlertCircle, ChevronDown, ChevronUp, Plus, Shield, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { AddressAutocomplete, type StructuredAddress } from '@/components/ui/AddressAutocomplete';
import {
  useCreateSecurity,
  useDeleteSecurity,
  useSecurities,
  useSecuritySummary,
} from '@/hooks/use-securities';
import { handleMutationError } from '@/lib/error-handling';
import { toast } from '@/stores/toast-store';
import {
  SECURITY_TYPE_LABELS,
  type SecurityItem,
  type SecuritySummary,
  type SecurityType,
} from '@/types';

function fmt(value?: string | null): string {
  if (!value) return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const ALL_SECURITY_TYPES = Object.keys(SECURITY_TYPE_LABELS) as SecurityType[];

interface Props {
  loanId: string;
}

export function SecurityScheduleTab({ loanId }: Props) {
  const { data: securities, isLoading, isError } = useSecurities(loanId);
  const { data: summary } = useSecuritySummary(loanId);
  const createMutation = useCreateSecurity(loanId);
  const deleteMutation = useDeleteSecurity(loanId);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Form state
  const [description, setDescription] = useState('');
  const [securityType, setSecurityType] = useState<SecurityType>('REAL_PROPERTY');
  const [priority, setPriority] = useState('1');
  const [priorEncumbrances, setPriorEncumbrances] = useState('0');
  const [borrowerValuation, setBorrowerValuation] = useState('');
  const [internalEstimate, setInternalEstimate] = useState('');
  const [valuerEstimate, setValuerEstimate] = useState('');
  const [valuationDate, setValuationDate] = useState('');
  const [valuerName, setValuerName] = useState('');
  const [existingDebt, setExistingDebt] = useState('');
  const [existingDebtHolder, setExistingDebtHolder] = useState('');
  const [notes, setNotes] = useState('');
  // Address autocomplete state (for REAL_PROPERTY)
  const [addressData, setAddressData] = useState<Partial<StructuredAddress>>({});

  function resetForm() {
    setDescription('');
    setSecurityType('REAL_PROPERTY');
    setPriority('1');
    setPriorEncumbrances('0');
    setBorrowerValuation('');
    setInternalEstimate('');
    setValuerEstimate('');
    setValuationDate('');
    setValuerName('');
    setExistingDebt('');
    setExistingDebtHolder('');
    setNotes('');
    setAddressData({});
    setShowForm(false);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    createMutation.mutate(
      {
        description: addressData.formattedAddress || addressData.addressLine1 || description,
        securityType,
        priority: parseInt(priority, 10),
        priorEncumbrances: parseInt(priorEncumbrances, 10),
        existingDebt: existingDebt ? parseFloat(existingDebt) : undefined,
        existingDebtHolder: existingDebtHolder || undefined,
        borrowerValuation: borrowerValuation ? parseFloat(borrowerValuation) : undefined,
        internalEstimate: internalEstimate ? parseFloat(internalEstimate) : undefined,
        valuerEstimate: valuerEstimate ? parseFloat(valuerEstimate) : undefined,
        valuationDate: valuationDate || undefined,
        valuerName: valuerName || undefined,
        notes: notes || undefined,
        // Address fields from autocomplete
        ...(securityType === 'REAL_PROPERTY' && addressData.addressLine1
          ? {
              addressLine1: addressData.addressLine1,
              addressLine2: addressData.addressLine2,
              suburb: addressData.suburb,
              state: addressData.state,
              postcode: addressData.postcode,
              country: addressData.country,
              latitude: addressData.latitude,
              longitude: addressData.longitude,
              placeId: addressData.placeId,
            }
          : {}),
      },
      {
        onSuccess: () => {
          toast.success('Security item added');
          resetForm();
        },
        onError: (err: any) => handleMutationError(err, 'Failed to add security item'),
      },
    );
  }

  function handleDelete(id: string) {
    deleteMutation.mutate(id, {
      onSuccess: () => toast.success('Security item removed'),
      onError: (err: any) => handleMutationError(err, 'Failed to remove security item'),
    });
  }

  // Compute totals
  const totals = (securities ?? []).reduce(
    (acc, s) => ({
      borrower: acc.borrower + (s.borrowerValuation ? parseFloat(s.borrowerValuation) : 0),
      internal: acc.internal + (s.internalEstimate ? parseFloat(s.internalEstimate) : 0),
      valuer: acc.valuer + (s.valuerEstimate ? parseFloat(s.valuerEstimate) : 0),
      existingDebt: acc.existingDebt + (s.existingDebt ? parseFloat(s.existingDebt) : 0),
    }),
    { borrower: 0, internal: 0, valuer: 0, existingDebt: 0 },
  );
  const bestEstimate = totals.valuer || totals.internal || totals.borrower;
  const netEquity = bestEstimate - totals.existingDebt;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading securities…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-20 text-red-500">
        <AlertCircle className="w-5 h-5 mr-2" />
        Failed to load securities
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <SummaryCard label="Total Items" value={String(securities?.length ?? 0)} />
        <SummaryCard label="Borrower Valuation" value={fmt(String(totals.borrower))} />
        <SummaryCard label="Internal Estimate" value={fmt(String(totals.internal))} />
        <SummaryCard label="Valuer Estimate" value={fmt(String(totals.valuer))} />
        <SummaryCard
          label="Existing Debt"
          value={fmt(String(totals.existingDebt))}
          warn={totals.existingDebt > 0}
        />
        <SummaryCard label="Net Equity" value={fmt(String(netEquity))} highlight />
      </div>

      {/* LTV & Risk Stack */}
      {summary && summary.itemCount > 0 && <LtvRiskStack summary={summary} />}

      {/* Header + add button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900">Security Schedule</h3>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 w-fit"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Security
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg border border-gray-200 p-5 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              {securityType === 'REAL_PROPERTY' ? (
                <AddressAutocomplete
                  value={addressData}
                  onChange={addr => {
                    setAddressData(addr);
                    setDescription(addr.formattedAddress || addr.addressLine1 || '');
                  }}
                  placeholder="Start typing a property address…"
                />
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    required
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="e.g. 2021 Toyota Hilux SR5"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                               placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                               focus:ring-blue-500 outline-none"
                  />
                </>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Security Type</label>
              <select
                value={securityType}
                onChange={e => setSecurityType(e.target.value as SecurityType)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              >
                {ALL_SECURITY_TYPES.map(t => (
                  <option key={t} value={t}>
                    {SECURITY_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <input
                type="number"
                min="1"
                value={priority}
                onChange={e => setPriority(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prior Encumbrances
              </label>
              <input
                type="number"
                min="0"
                value={priorEncumbrances}
                onChange={e => setPriorEncumbrances(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Existing Debt ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={existingDebt}
                onChange={e => setExistingDebt(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Debt Holder</label>
              <input
                value={existingDebtHolder}
                onChange={e => setExistingDebtHolder(e.target.value)}
                placeholder="e.g. CBA, Westpac"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Borrower Valuation ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={borrowerValuation}
                onChange={e => setBorrowerValuation(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Internal Estimate ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={internalEstimate}
                onChange={e => setInternalEstimate(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valuer Estimate ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={valuerEstimate}
                onChange={e => setValuerEstimate(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valuation Date</label>
              <input
                type="date"
                value={valuationDate}
                onChange={e => setValuationDate(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valuer Name</label>
              <input
                value={valuerName}
                onChange={e => setValuerName(e.target.value)}
                placeholder="Independent valuer"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Additional notes about this security…"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                         placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                         focus:ring-blue-500 outline-none resize-none"
            />
          </div>

          {createMutation.isError && (
            <p className="text-sm text-red-600">Failed to add security item</p>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300
                         rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <Button
              variant="primary"
              type="submit"
              disabled={createMutation.isPending}
              className="flex items-center gap-2 w-fit"
            >
              {createMutation.isPending && <Spinner size="lg" className="w-4 h-4 animate-spin" />}
              Add Security
            </Button>
          </div>
        </form>
      )}

      {/* Security items list */}
      {!securities || securities.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 py-12 text-center text-gray-400">
          <Shield className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">No security items recorded</p>
          <p className="text-xs mt-1">Add collateral and security taken against this loan</p>
        </div>
      ) : (
        <div className="space-y-2">
          {securities.map(item => (
            <SecurityItemCard
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
              onDelete={() => handleDelete(item.id)}
              deleting={deleteMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────

function SummaryCard({
  label,
  value,
  highlight,
  warn,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  warn?: boolean;
}) {
  const borderClass = highlight
    ? 'border-indigo-200 bg-indigo-50'
    : warn
      ? 'border-amber-200 bg-amber-50'
      : 'border-gray-200 bg-white';
  const textClass = highlight ? 'text-indigo-700' : warn ? 'text-amber-700' : 'text-gray-900';

  return (
    <div className={`rounded-lg border p-3 ${borderClass}`}>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-lg font-semibold ${textClass}`}>{value}</p>
    </div>
  );
}

function SecurityItemCard({
  item,
  expanded,
  onToggle,
  onDelete,
  deleting,
}: {
  item: SecurityItem;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const bestVal = item.valuerEstimate ?? item.internalEstimate ?? item.borrowerValuation;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex flex-col items-center shrink-0">
          <span
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              item.priority === 1
                ? 'bg-green-100 text-green-700'
                : item.priority === 2
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-red-100 text-red-700'
            }`}
          >
            {item.priority}
          </span>
          <span className="text-[9px] text-gray-400 mt-0.5">
            {item.priority === 1 ? '1st' : item.priority === 2 ? '2nd' : `${item.priority}th`}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">{item.description}</p>
          <p className="text-xs text-gray-400">
            {SECURITY_TYPE_LABELS[item.securityType] ?? item.securityType}
            {item.priorEncumbrances > 0 && (
              <span className="text-amber-500">{` · ${item.priorEncumbrances} ranking ahead`}</span>
            )}
            {item.existingDebt && parseFloat(item.existingDebt) > 0 && (
              <span className="text-red-400">
                {` · ${fmt(item.existingDebt)} prior debt`}
                {item.existingDebtHolder && ` (${item.existingDebtHolder})`}
              </span>
            )}
          </p>
        </div>
        <div className="text-right shrink-0 mr-2">
          <p className="text-sm font-medium text-gray-900">{fmt(bestVal)}</p>
          <p className="text-[10px] text-gray-400">best estimate</p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        )}
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-3">
          <div className="grid grid-cols-3 gap-4">
            <ValuationField
              label="Borrower Valuation"
              value={fmt(item.borrowerValuation)}
              type="borrower"
            />
            <ValuationField
              label="Internal Estimate"
              value={fmt(item.internalEstimate)}
              type="internal"
            />
            <ValuationField
              label="Valuer Estimate"
              value={fmt(item.valuerEstimate)}
              type="valuer"
            />
          </div>

          {/* Existing indebtedness */}
          {item.existingDebt && parseFloat(item.existingDebt) > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-[10px] font-medium text-amber-600 mb-1">Existing Indebtedness</p>
              <div className="flex items-baseline gap-4">
                <p className="text-sm font-semibold text-amber-800">{fmt(item.existingDebt)}</p>
                {item.existingDebtHolder && (
                  <p className="text-xs text-amber-600">held by {item.existingDebtHolder}</p>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-xs text-gray-500">Valuation Date</span>
              <p className="text-gray-900">{fmtDate(item.valuationDate)}</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Valuer</span>
              <p className="text-gray-900">{item.valuerName || '—'}</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Status</span>
              <p className="text-gray-900 capitalize">{item.status.toLowerCase()}</p>
            </div>
          </div>

          {item.notes && (
            <div>
              <span className="text-xs text-gray-500">Notes</span>
              <p className="text-sm text-gray-700 mt-0.5">{item.notes}</p>
            </div>
          )}

          <div className="flex justify-end pt-1">
            <button
              onClick={onDelete}
              disabled={deleting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600
                         hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ValuationField({
  label,
  value,
  type,
}: {
  label: string;
  value: string;
  type: 'borrower' | 'internal' | 'valuer';
}) {
  const colors = {
    borrower: 'bg-amber-50 text-amber-700 border-amber-200',
    internal: 'bg-blue-50 text-blue-700 border-blue-200',
    valuer: 'bg-green-50 text-green-700 border-green-200',
  };

  return (
    <div className={`rounded-lg border p-2.5 ${colors[type]}`}>
      <p className="text-[10px] font-medium opacity-70 mb-0.5">{label}</p>
      <p className="text-sm font-semibold">{value}</p>
    </div>
  );
}

// ── LTV & Risk Stack Visualization ──────────────────────

function fmtAud(num: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function ltvColor(ltv: number): string {
  if (ltv <= 60) return 'text-green-700';
  if (ltv <= 80) return 'text-amber-600';
  return 'text-red-600';
}

function ltvBg(ltv: number): string {
  if (ltv <= 60) return 'bg-green-100 border-green-200';
  if (ltv <= 80) return 'bg-amber-100 border-amber-200';
  return 'bg-red-100 border-red-200';
}

function LtvRiskStack({ summary }: { summary: SecuritySummary }) {
  const { riskStack, seniorLtv, totalLtv, ourLtv, securityCover } = summary;
  const totalValue = riskStack.totalValue || 1; // prevent division by zero

  // Calculate percentages for the stacked bar
  const seniorPct = Math.min((riskStack.seniorDebt / totalValue) * 100, 100);
  const ourPct = Math.min((riskStack.ourTranche / totalValue) * 100, 100 - seniorPct);
  const equityPct = Math.max(0, 100 - seniorPct - ourPct);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
      <h3 className="text-sm font-semibold text-gray-900">Loan-to-Value & Risk Position</h3>

      {/* LTV metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={`rounded-lg border p-3 ${ltvBg(seniorLtv)}`}>
          <p className="text-[10px] font-medium text-gray-500 mb-0.5">Senior LTV</p>
          <p className={`text-lg font-bold ${ltvColor(seniorLtv)}`}>{seniorLtv.toFixed(1)}%</p>
          <p className="text-[10px] text-gray-400">Prior debt / Value</p>
        </div>
        <div className={`rounded-lg border p-3 ${ltvBg(totalLtv)}`}>
          <p className="text-[10px] font-medium text-gray-500 mb-0.5">Total LTV</p>
          <p className={`text-lg font-bold ${ltvColor(totalLtv)}`}>{totalLtv.toFixed(1)}%</p>
          <p className="text-[10px] text-gray-400">All debt / Value</p>
        </div>
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
          <p className="text-[10px] font-medium text-gray-500 mb-0.5">Our LTV</p>
          <p className="text-lg font-bold text-indigo-700">{ourLtv.toFixed(1)}%</p>
          <p className="text-[10px] text-gray-400">Our loan / Value</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-[10px] font-medium text-gray-500 mb-0.5">Security Cover</p>
          <p className="text-lg font-bold text-gray-900">{securityCover.toFixed(2)}x</p>
          <p className="text-[10px] text-gray-400">Net equity / Our loan</p>
        </div>
      </div>

      {/* Stacked bar - Risk Stack visualization */}
      <div>
        <p className="text-xs font-medium text-gray-600 mb-2">Risk Stack Position</p>
        <div className="h-10 rounded-lg overflow-hidden flex border border-gray-200">
          {seniorPct > 0 && (
            <div
              className="bg-red-400 flex items-center justify-center transition-all"
              style={{ width: `${seniorPct}%` }}
              title={`Senior Debt: ${fmtAud(riskStack.seniorDebt)}`}
            >
              {seniorPct > 12 && (
                <span className="text-[10px] font-semibold text-white truncate px-1">Senior</span>
              )}
            </div>
          )}
          {ourPct > 0 && (
            <div
              className="bg-indigo-500 flex items-center justify-center transition-all"
              style={{ width: `${ourPct}%` }}
              title={`Our Loan: ${fmtAud(riskStack.ourTranche)}`}
            >
              {ourPct > 10 && (
                <span className="text-[10px] font-semibold text-white truncate px-1">Our</span>
              )}
            </div>
          )}
          {equityPct > 0 && (
            <div
              className="bg-green-400 flex items-center justify-center transition-all"
              style={{ width: `${equityPct}%` }}
              title={`Equity: ${fmtAud(riskStack.availableEquity)}`}
            >
              {equityPct > 12 && (
                <span className="text-[10px] font-semibold text-white truncate px-1">Equity</span>
              )}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
          {riskStack.seniorDebt > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-red-400" />
              Senior: {fmtAud(riskStack.seniorDebt)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm bg-indigo-500" />
            Our Loan: {fmtAud(riskStack.ourTranche)}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm bg-green-400" />
            Equity: {fmtAud(riskStack.availableEquity)}
          </span>
          <span className="ml-auto text-gray-400">Total: {fmtAud(totalValue)}</span>
        </div>
      </div>
    </div>
  );
}
